/**
 * **발견사항 가시성 술어 + 개선대책 선택 판정** — 서버가 전수를 내려주므로 «세는 쪽»이 걸러야 한다.
 *
 * ⚠️ **축이 둘이다 — 파일 이름은 하나만 말한다.** export 5개 중 셋은 가시성
 * (`isActiveFinding`·`activeFindings`·`countActiveFindings`)이고 **둘은 「개선대책이 선택됐나」**
 * (`hasSelectedMeasure`·`countSelectedMeasureFindings`)다. 후자는 가시성 필터를 **안에서** 걸어
 * 두 축을 한 번에 답하는 자리이고, 그래서 이름이 `findingVisibility`인 파일에 산다.
 *
 * 이 한 줄이 필요한 이유는 이 모듈의 존재 이유와 같다: 「개선대책이 선택됐나」를 찾는 사람이
 * 파일 이름만 보고 이 파일을 **열지 않으면** 자기 술어를 또 쓴다. 그것이 정확히 2026-10-07에
 * 고친 상태였다(손으로 흩뿌린 술어 13자리 + 게이트 둘이 필터를 빠뜨림). 파일명을 바꾸지 않는
 * 이유는 import 전수와 배선 래칫(`__tests__/findingVisibilityWiring.test.ts`의 needle)을 함께
 * 건드려야 해서다 — 이름 대신 **이 줄**이 그 일을 한다.
 *
 * 점검 상세(`GET /inspections/:id`)는 발견사항을 **삭제분까지 전수로** 내려준다. 그것이 계약이고
 * 실수가 아니다(근거는 `apps/server/src/inspections/inspections.service.ts`의 `findings` where
 * 주석): 그 응답이 **복원 입구의 유일한 공급원**이라, 삭제분을 빼면 `FindingDetail`의 「삭제됨 +
 * 되돌리기」가 렌더될 수 없어 복원이 도달 불가가 된다.
 *
 * ⚠️ **그래서 화면에는 두 종류의 소비처가 있고, 섞으면 둘 중 하나가 깨진다.**
 *
 * | 소비처 | 무엇을 받아야 하나 | 예 |
 * |---|---|---|
 * | **세는·판정하는 자리** | 살아 있는 것만 (이 모듈) | 보고서 생성 게이트 · 완료 서명 게이트 · 「N/M건 포함」 |
 * | **그리는 자리** | **전수** — 삭제분이 입력이다 | `PhotoCardVertical`(「삭제됨」 배너·삭제 건수 배지·번호 매김이 삭제분을 건너뛰며 돈다) |
 *
 * 즉 `PhotoCardVertical`류는 이 모듈을 쓰면 **안 된다**. 거기서 삭제분을 거르면 되돌리기 버튼과
 * 삭제 건수 배지가 사라진다.
 *
 * ⚠️ **왜 공용 모듈인가** — `!f.deletedAt`가 web 10곳에 손으로 흩어져 있었고, 그래서 **세는 자리
 * 13곳이 빠진 채** 남아 있었다(2026-10-07 전수 대조). 손으로 흩뿌린 술어는 「지금은 맞다」만
 * 보장한다.
 *
 * ⚠️ **다른 모듈에 끼워 넣지 않고 새 파일로 둔 이유**: vitest의 팩토리 `vi.mock`은 부분 mock이
 * 아니라 팩토리가 돌려준 키만 존재하게 한다. 이미 mock되는 모듈(`lib/organizations`류)에 새
 * export를 더하면 그 모듈을 mock하는 **무관한 기존 spec 전부**가 `No "X" export is defined on the
 * mock`으로 죽는다(선례 `lib/memberStatus.ts` 분리).
 */

/** 가시성 판정에 필요한 최소 형상 — 레이아웃 `Finding`과 `lib/inspections`의 `InspectionFinding` 둘 다 만족한다. */
export interface FindingVisibility {
  deletedAt?: string | Date | null;
}

/** 개선대책 보유 판정에 필요한 최소 형상. `selectedMeasures`는 jsonb라 배열이 아닐 수 있다. */
export interface FindingWithMeasures extends FindingVisibility {
  selectedMeasures?: unknown;
}

/** 살아 있는 발견사항인가 — soft delete되지 않았나. */
export function isActiveFinding(finding: FindingVisibility): boolean {
  return !finding.deletedAt;
}

/** 살아 있는 발견사항만 남긴다. */
export function activeFindings<T extends FindingVisibility>(findings: readonly T[]): T[] {
  return findings.filter(isActiveFinding);
}

/** 살아 있는 발견사항 수 — 「N/M건 포함」의 분모·사진 캡션이 쓴다. */
export function countActiveFindings(findings: readonly FindingVisibility[]): number {
  return activeFindings(findings).length;
}

/**
 * 발견사항 **하나**에 개선대책이 선택됐나 — 아래 두 함수의 공통 술어.
 *
 * ⚠️ 판정은 `Array.isArray`로 한다(종전 레이아웃의 `selectedMeasures?.length`가 아니다) —
 * `selectedMeasures`는 jsonb라 배열이 아닌 값이 올 수 있고, 그때 `?.length`는 「길이가 있으면
 * 참」으로 새는 반면 이 쪽은 **배열이 아니면 거짓**이다. 훅(`useInspectionDetailPage`)이 이미
 * 그 형태였으므로 두 자리의 의미를 여기서 하나로 모은다.
 *
 * ⚠️ **가시성은 여기서 보지 않는다** — 이 술어는 「개선대책」 축 하나만 답하고, 삭제분 제외는
 * 호출부의 `activeFindings()`가 소유한다. 두 축을 한 함수에 섞으면 둘 중 하나를 되돌려도
 * 같은 모양으로 틀려서 어느 축이 깨졌는지 프로브로 가를 수 없다.
 *
 * ⚠️ 이 모듈 **밖으로 내보내지 않는다.** 밖에서 쓰고 싶어지면 대개 「살아 있는 것만」을 함께
 * 물어야 하는 자리이므로 `hasSelectedMeasure`/`countSelectedMeasureFindings`를 쓸 것 —
 * 이 술어만 빼 쓰면 가시성 필터가 다시 빠진다(이 모듈이 생긴 원인 그 자체다).
 */
function hasMeasure(finding: FindingWithMeasures): boolean {
  const measures = finding.selectedMeasures;
  return Array.isArray(measures) && measures.length > 0;
}

/**
 * 개선대책이 **하나 이상 선택된 살아 있는** 발견사항이 있나 — 보고서 생성·완료 서명 게이트가 쓴다.
 *
 * 판정 규약은 `hasMeasure`가 소유한다 — 여기서 `Array.isArray`를 다시 쓰지 말 것(2026-10-08까지
 * 이 파일 **안에서** 그 두 줄이 두 벌이었다. 「손으로 흩뿌린 술어는 『지금은 맞다』만 보장한다」를
 * 선언하는 모듈이 자기 안에서 그러고 있었다).
 */
export function hasSelectedMeasure(findings: readonly FindingWithMeasures[]): boolean {
  return activeFindings(findings).some(hasMeasure);
}

/** 개선대책이 선택된 **살아 있는** 발견사항 수 — 「N/M건 포함」의 분자가 쓴다. */
export function countSelectedMeasureFindings(findings: readonly FindingWithMeasures[]): number {
  return activeFindings(findings).filter(hasMeasure).length;
}
